import React from "react";
import { Star, MapPin, Facebook, Users, ShoppingBag } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { reviewService } from "@/api/reviewService";
import { useAuth } from "@/contexts/AuthContext";
import { PerSourceStat } from "@/types/review";

type GooglePlaces = {
  placeId: string;
  name: string;
  address: string;
  rating: number;
  reviewCount: number;
};

// Platform-claimed rating preserved at store.metadata.platformRating —
// never conflated with the real Yetfarrag review count
export type PlatformRating = {
  source: string;
  rating: number;
  reviewCount: number;
};

interface ReviewsSummaryProps {
  storeId: string;
  storeName: string;
  categorySlug?: string;
  googlePlaces?: GooglePlaces;
  platformRating?: PlatformRating;
  onAddReview?: () => void;
}

const sourceConfig: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
  yetfarrag: {
    label: "يتفرج",
    color: "text-emerald-600",
    icon: <Star className="w-4 h-4 fill-emerald-500 text-emerald-500" />,
  },
  google_maps: {
    label: "Google Maps",
    color: "text-red-600",
    icon: <MapPin className="w-4 h-4 text-red-500" />,
  },
  facebook: {
    label: "Facebook",
    color: "text-blue-600",
    icon: <Facebook className="w-4 h-4 text-blue-500" />,
  },
  facebook_group: {
    label: "مجموعات Facebook",
    color: "text-blue-700",
    icon: <Users className="w-4 h-4 text-blue-600" />,
  },
  facebook_group_post: {
    label: "منشورات مجموعات Facebook",
    color: "text-blue-700",
    icon: <Users className="w-4 h-4 text-blue-600" />,
  },
  facebook_group_comment: {
    label: "تعليقات مجموعات Facebook",
    color: "text-blue-700",
    icon: <Users className="w-4 h-4 text-blue-600" />,
  },
  // Scraped from the merchant's on-site Shopify review app (Loox) — see
  // ReviewCard.getSourceInfo for why the internal source and the shown label differ.
  loox: {
    label: "متجر شوبيفاي",
    color: "text-lime-700",
    icon: <ShoppingBag className="w-4 h-4 text-lime-600" />,
  },
};

const getSourceConfig = (source: string) =>
  sourceConfig[source] ?? {
    label: source,
    color: "text-gray-600",
    icon: <Star className="w-4 h-4 text-gray-400" />,
  };

// Muted, clearly-labeled line for platform-CLAIMED counts (Google/Facebook) —
// visually distinct from the real Yetfarrag review count, and shown even when
// the store has zero real reviews
const PlatformClaimedCounts: React.FC<{
  googlePlaces?: GooglePlaces;
  platformRating?: PlatformRating;
  className?: string;
}> = ({ googlePlaces, platformRating, className }) => {
  const claims: { key: string; label: string; rating: number; reviewCount: number }[] = [];

  if (googlePlaces?.reviewCount) {
    claims.push({
      key: "google",
      label: "جوجل",
      rating: googlePlaces.rating || 0,
      reviewCount: googlePlaces.reviewCount,
    });
  }

  if (platformRating && (platformRating.reviewCount > 0 || platformRating.rating > 0)) {
    claims.push({
      key: platformRating.source,
      label: platformRating.source === "facebook" ? "فيسبوك" : platformRating.source,
      rating: platformRating.rating || 0,
      reviewCount: platformRating.reviewCount || 0,
    });
  }

  if (claims.length === 0) return null;

  return (
    <p className={`text-xs text-gray-400 font-arabic ${className || ""}`}>
      التقييمات المعلنة على المنصات:{" "}
      {claims.map((claim, index) => (
        <span key={claim.key} className="whitespace-nowrap">
          {index > 0 && " · "}
          {claim.label} {claim.rating.toFixed(1)} ({claim.reviewCount})
        </span>
      ))}
    </p>
  );
};

const ReviewsSummary: React.FC<ReviewsSummaryProps> = ({
  storeId,
  storeName,
  googlePlaces,
  platformRating,
  onAddReview,
}) => {
  const { user } = useAuth();

  // request() unwraps { ok, data: ReviewStats } → ReviewStats, so stats is directly ReviewStats
  const { data: stats } = useQuery({
    queryKey: ["reviewStats", storeId],
    queryFn: () => reviewService.getStoreReviewStats({ storeId }),
    staleTime: 5 * 60 * 1000,
  });

  if (!stats || stats.totalReviews === 0) {
    return (
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="font-arabic">التقييمات</CardTitle>
            {user && onAddReview && (
              <Button size="sm" onClick={onAddReview} className="font-arabic">
                <Plus className="w-4 h-4 mr-1" />
                أضف تقييمك
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-2">
          <p className="text-sm text-gray-500 font-arabic text-center py-2">
            لا توجد تقييمات بعد
          </p>
          <PlatformClaimedCounts
            googlePlaces={googlePlaces}
            platformRating={platformRating}
          />
        </CardContent>
      </Card>
    );
  }

  // Build per-source list — replace google maps imported count with full Places count if available
  const perSource: PerSourceStat[] = stats.perSource ?? [];

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="font-arabic">التقييمات</CardTitle>
          {user && onAddReview && (
            <Button size="sm" onClick={onAddReview} className="font-arabic">
              <Plus className="w-4 h-4 mr-1" />
              أضف تقييمك
            </Button>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-5">
        {/* Combined overall score */}
        <div className="text-center">
          <div className="flex items-center justify-center gap-2">
            <Star className="w-7 h-7 fill-yellow-400 text-yellow-400" />
            <span className="text-4xl font-bold">
              {stats.averageRating.toFixed(1)}
            </span>
          </div>
          <p className="text-sm text-gray-500 font-arabic mt-1">
            بناءً على {stats.totalReviews} تقييم
          </p>
        </div>

        {/* Rating distribution */}
        <div className="space-y-1.5">
          {[5, 4, 3, 2, 1].map((star) => {
            const count = stats.ratingDistribution[star] || 0;
            const pct = stats.totalReviews > 0 ? (count / stats.totalReviews) * 100 : 0;
            return (
              <div key={star} className="flex items-center gap-2 text-sm">
                <span className="w-3 text-center text-gray-600">{star}</span>
                <Star className="w-3.5 h-3.5 fill-yellow-400 text-yellow-400 shrink-0" />
                <div className="flex-1 bg-gray-100 rounded-full h-1.5">
                  <div
                    className="bg-yellow-400 h-1.5 rounded-full transition-all"
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <span className="text-gray-400 w-6 text-right text-xs">{count}</span>
              </div>
            );
          })}
        </div>

        {/* Per-source breakdown */}
        {perSource.length > 0 && (
          <div className="space-y-2 pt-1 border-t">
            <p className="text-xs font-medium text-gray-500 font-arabic">تفاصيل المصادر</p>
            {perSource.map((src) => {
              const cfg = getSourceConfig(src.source);
              // For google maps, show full Places count if available
              const isGoogle = src.source === "google_maps";
              const displayCount = isGoogle && googlePlaces?.reviewCount
                ? googlePlaces.reviewCount
                : src.count;
              const displayRating = isGoogle && googlePlaces?.rating
                ? googlePlaces.rating
                : src.averageRating;

              return (
                <div key={src.source} className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    {cfg.icon}
                    <span className={`font-arabic ${cfg.color}`}>{cfg.label}</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-600">
                    <span className="text-xs">{displayRating.toFixed(1)}</span>
                    <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                    <span className="text-xs text-gray-400">({displayCount})</span>
                  </div>
                </div>
              );
            })}

            {/* Show Google aggregate note if we imported fewer than total */}
            {googlePlaces?.reviewCount && (() => {
              const importedGoogle = perSource.find(s => s.source === "google_maps");
              if (importedGoogle && importedGoogle.count < googlePlaces.reviewCount) {
                return (
                  <p className="text-xs text-gray-400 font-arabic">
                    * تم استيراد {importedGoogle.count} تقييم من أصل {googlePlaces.reviewCount} على Google
                  </p>
                );
              }
              return null;
            })()}
          </div>
        )}

        {/* Platform-claimed counts — labeled and muted, never mixed with the real count */}
        <PlatformClaimedCounts
          googlePlaces={googlePlaces}
          platformRating={platformRating}
          className="pt-2 border-t"
        />
      </CardContent>
    </Card>
  );
};

export default ReviewsSummary;
